import { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/utils/money";
import { cn } from "@repo/design-system/utils";
import { getHandlingTimeLabel } from "@repo/api-sdk";
import type { ComparisonCell } from "@/utils/supplierComparison";

export interface SupplierComparisonCellProps {
  cell: ComparisonCell;
  /** Whether this is the supplier the customer currently buys this product from. */
  isCurrentSupplier: boolean;
  /** Whether this is the cheapest offer shown for this product. */
  isCheapest: boolean;
  /**
   * Show the offer's handling time in the cell. Off by default: it's normally
   * uniform per supplier and stated once in the column header, so it's only
   * needed here for a supplier whose offers actually disagree.
   */
  showHandlingTime?: boolean;
  /** Called when the buyer clicks an eligible cell to switch to this supplier. */
  onClick?: () => void;
  /** This row's switch is in flight (any cell in the row, not just this one). */
  isPending?: boolean;
}

/**
 * Shared box geometry for both cell states. The `min-h` has to match across
 * them or cells in a row stop lining up - it reserves room for the badge row
 * even when none renders (price + badge + padding), so a "Cheapest" cell
 * isn't taller than its neighbours and the grid doesn't rely on the browser
 * stretching every cell to its tallest sibling.
 */
const CELL_BOX = "h-full min-h-[60px] w-full rounded-md p-2";

/**
 * One product x supplier cell: a price with a small "cheapest" tag, or a
 * muted "not stocked" placeholder when this supplier doesn't carry the
 * product. The current pick is shown via the purple ring alone - no separate
 * "Your pick" label, since it's the only ringed cell. Handling time normally
 * lives in the column header (it's per-supplier in practice), and only
 * appears here via `showHandlingTime` when it varies within a column.
 *
 * Clickable only when it's a stocked, active, non-current offer - clicking it
 * switches the product to this supplier (see `useSwitchSupplier`). Inactive/
 * out-of-stock offers and the current pick itself stay display-only.
 */
export const SupplierComparisonCell = memo(function SupplierComparisonCell({
  cell,
  isCurrentSupplier,
  isCheapest,
  showHandlingTime,
  onClick,
  isPending
}: SupplierComparisonCellProps) {
  if (cell.kind === "not-stocked") {
    return (
      <div className={cn(CELL_BOX, "flex items-center")}>
        <span className="text-xs italic text-gray-400">Not stocked</span>
      </div>
    );
  }

  if (cell.kind === "current-unpriced") {
    // Never clickable - there's no offer id to switch to, and nothing to
    // compare it against without the other suppliers' pricing either.
    return (
      <div
        className={cn(
          CELL_BOX,
          isCurrentSupplier && "ring-2 ring-purple-500 ring-offset-1"
        )}
      >
        <div className="font-semibold text-gray-900">
          {formatPrice(cell.unitPrice)}
        </div>
        <div className="mt-0.5 text-xs italic text-gray-400">
          Pricing unavailable
        </div>
      </div>
    );
  }

  const { offer } = cell;
  // Eligibility is decided once by `switchTargetFor` (in the grid table), which
  // only wires `onClick` for a switchable cell - so this is clickable iff we
  // were handed a handler. No status / current-supplier re-check lives here.
  const isClickable = !!onClick;

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        CELL_BOX,
        // `transition-all` (not just `transition-colors`) so the ring itself -
        // a box-shadow, not a color property - fades in/out too. Matters most
        // right after a switch: the "current supplier" ring moves to a new
        // cell, and without this it snaps instead of easing, which reads as
        // another small jump on top of whatever caused the switch itself.
        "transition-all duration-200",
        isCurrentSupplier && "ring-2 ring-purple-500 ring-offset-1",
        isClickable &&
          "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500",
        // Blocks a second click mid-switch without dimming: the ring has
        // already moved to the clicked supplier optimistically, and greying
        // the row out on top of that just makes a confirmed action look like
        // it's still loading for the rest of the round trip.
        isPending && "pointer-events-none"
      )}
    >
      <div className="font-semibold text-gray-900">
        {formatPrice(offer.calculated_price.calculated_amount)}
      </div>
      {showHandlingTime && (
        <div className="mt-0.5 text-xs text-gray-500">
          {getHandlingTimeLabel(offer.handling_time)}
        </div>
      )}
      {isCheapest && (
        <Badge variant="success" className="mt-1 px-1.5 py-0 text-[10px]">
          Cheapest
        </Badge>
      )}
    </div>
  );
});
